import { CacheableRolodexEntryJson } from 'types/rolodex/rolodexCache'
import {
  RolodexEntryDataType,
  RolodexEntryIndexType,
  RolodexEntryJson,
  RolodexEntryTagsType,
  RolodexEntryType,
} from 'types/rolodex/rolodexEntries'

/**
 * The base interface from which all Gecko<NAME> objects inherit (i.e. GeckoJob)
 */
export interface GeckoEntry {
  /** The unique ID for this entry */
  readonly id: string

  /** The  entry "type", similar to a "class" or "kind" */
  readonly type: RolodexEntryType

  /** Data associated with this  entry */
  readonly data: RolodexEntryDataType

  /** A record of IDs per type associated to this entry */
  readonly tags: RolodexEntryTagsType

  /** An index used for efficient file lookup */
  readonly index: RolodexEntryIndexType

  /** A numeric identifier tracking updates to an entry or its files */
  readonly generation: number

  /** A string field that represents when the entry was created */
  readonly created_at?: string

  /** A string field that represents when the entry was last updated */
  readonly updated_at: string

  // TODO PAC: reconsider this
  readonly raw: RolodexEntryJson

  /** Whether this entry is from the local cache */
  readonly isFromCache: boolean

  /** A bool whether the entry is visible to customers, values are "internal" or "none" */
  readonly access_restriction: 'internal' | null
}

/**
 * A templated, abstract base class which "Gecko<NAME>" objects can inherit from
 */
export abstract class GeckoEntryBase<
  DataType extends RolodexEntryDataType = RolodexEntryDataType,
  TagsType extends RolodexEntryTagsType = RolodexEntryTagsType,
  IndexType extends RolodexEntryIndexType = RolodexEntryIndexType,
> implements GeckoEntry {
  //----------------------------------------------------------------------------

  /** The -wide unique ID for this entry */
  public readonly id: string

  /** The  entry "type", similar to a "class" or "kind" */
  public readonly type: RolodexEntryType

  /** Data associated with this  entry */
  public readonly data: DataType

  /** A record of IDs per type associated to this entry */
  public readonly tags: TagsType

  /** An index used for efficient file lookup */
  public readonly index: IndexType

  /** A numeric identifier tracking updates to an entry or its files */
  public readonly generation: number

  /** A string field that represents when the entry was created */
  readonly created_at?: string

  /** A string field that represents when the entry was last updated */
  public readonly updated_at: string

  /** A string field that represents the user who created the entry */
  public readonly created_by?: string

  /** A string field that represents the user who last updated the entry */
  public readonly last_updated_by?: string

  /** Whether this entry is from the local cache */
  public readonly isFromCache: boolean

  // TODO PAC: reconsider this
  readonly raw: RolodexEntryJson

  /** A bool whether the entry is visible to customers, values are "internal" or "none" */
  public readonly access_restriction: 'internal' | null

  //----------------------------------------------------------------------------

  /**
   * A protected constructor used by subclasses of this one
   * @param json    The raw JSON to create an instance from
   * @param data    Data to attach to this entry
   * @param tags    Tags used to link this entry to other entries
   * @param index   An optional index used for fast image lookup
   * @protected
   */
  protected constructor(
    json: CacheableRolodexEntryJson,
    data: DataType,
    tags: TagsType,
    index: IndexType
  ) {
    this.id = json.id
    this.type = json.type

    // Assume all data from the JSON unless it's overwritten when calling super()
    this.data = { ...json.data, ...data }
    this.tags = { ...json.tags, ...tags }
    this.index = { ...json.index, ...index }
    this.generation = json.generation
    this.created_at = json.created_at
    this.updated_at = json.updated_at
    this.created_by = json.created_by
    this.last_updated_by = json.last_updated_by

    const { isFromCache, ...raw } = json
    this.raw = { ...raw }
    this.isFromCache = isFromCache ?? false
    this.access_restriction =
      json.access_restriction === 'internal' ? 'internal' : null
  }
}
